/**
 * authentikOidc.js
 *
 * Module-level OIDC/PKCE client for Authentik.
 *
 * Exports:
 *   - login({ email, returnTo, flow }) , kicks off PKCE redirect
 *   - handleCallback()                 , exchanges ?code= for tokens
 *   - getToken()                       , returns id_token (refreshing if needed)
 *   - isAuthenticated()                , synchronous check
 *   - getUser()                        , decoded id_token claims
 *   - logout()                         , clears storage, redirects to Authentik
 *
 * Token-storage uses localStorage with the prefix `hai_job_auth_`.
 *
 * Per design §2.1 the *id_token* is used as the API bearer because
 * Authentik issues opaque access-tokens by default.
 */

import { decodeJwt } from 'jose';
import { isConsumed, markConsumed } from './consumedCodes';
import { scheduleRefresh, cancelRefresh } from './silentRefresh';

// ---------------------------------------------------------------------------
// Env (read once at module-load).
// ---------------------------------------------------------------------------

const baseUrl = process.env.REACT_APP_AUTHENTIK_BASE_URL;
const clientId = process.env.REACT_APP_AUTHENTIK_CLIENT_ID;
const appSlug = process.env.REACT_APP_AUTHENTIK_APP_SLUG;
const redirectUri = process.env.REACT_APP_AUTHENTIK_REDIRECT_URI;

if (!baseUrl || !clientId || !appSlug || !redirectUri) {
  // Niet-fataal: op de publieke talents-demo is er bewust geen login geconfigureerd,
  // dus dit is verwacht (geen rode console-error). Wel zichtbaar als info zodat een
  // echte deploy mét login het opmerkt.
  console.info(
    '[auth] Authentik env-vars niet gezet — login uitgeschakeld (verwacht op publieke demo). ' +
      'Voor login: REACT_APP_AUTHENTIK_BASE_URL, REACT_APP_AUTHENTIK_CLIENT_ID, ' +
      'REACT_APP_AUTHENTIK_APP_SLUG, REACT_APP_AUTHENTIK_REDIRECT_URI'
  );
}

// ---------------------------------------------------------------------------
// Storage keys.
// ---------------------------------------------------------------------------

const LS = {
  idToken: 'hai_job_auth_id_token',
  accessToken: 'hai_job_auth_access_token',
  refreshToken: 'hai_job_auth_refresh_token',
  expiresAt: 'hai_job_auth_expires_at',
  viaMs: 'hai_job_auth_via_ms',
};

const SS = {
  pkceVerifier: 'auth_pkce_verifier',
  state: 'auth_state',
  returnTo: 'auth_return_to',
};

// All keys that should be wiped on logout / refresh-failure.
const ALL_LS_KEYS = Object.values(LS);

// ---------------------------------------------------------------------------
// PKCE helpers (Web Crypto, no extra lib).
// ---------------------------------------------------------------------------

function randomUrlSafe(length = 64) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

function base64UrlEncode(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

async function deriveChallenge(verifier) {
  const hash = await sha256(verifier);
  return base64UrlEncode(hash);
}

// ---------------------------------------------------------------------------
// Storage helpers.
// ---------------------------------------------------------------------------

function clearAuthStorage() {
  cancelRefresh();
  for (const k of ALL_LS_KEYS) {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  }
}

function persistTokens({ id_token, access_token, refresh_token, expires_in }) {
  const expiresAt = Date.now() + (Number(expires_in) || 300) * 1000;
  try {
    if (id_token) localStorage.setItem(LS.idToken, id_token);
    if (access_token) localStorage.setItem(LS.accessToken, access_token);
    if (refresh_token) localStorage.setItem(LS.refreshToken, refresh_token);
    localStorage.setItem(LS.expiresAt, String(expiresAt));
  } catch (err) {
    console.error('[auth] failed to persist tokens:', err);
  }
  return expiresAt;
}

function getStoredExpiry() {
  const v = localStorage.getItem(LS.expiresAt);
  if (!v) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ---------------------------------------------------------------------------
// Token endpoint helpers.
// ---------------------------------------------------------------------------

function tokenEndpoint() {
  return `${baseUrl}/application/o/token/`;
}

async function postToken(params) {
  const body = new URLSearchParams(params);
  const res = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    let detail = '';
    let parsed = null;
    try {
      detail = await res.text();
      // Token endpoint always returns JSON on errors per RFC 6749, but we
      // tolerate non-JSON bodies (e.g. an upstream proxy 502).
      try {
        parsed = detail ? JSON.parse(detail) : null;
      } catch {
        parsed = null;
      }
    } catch {
      // ignore
    }
    const err = new Error(
      `token_request_failed: ${res.status}${detail ? ` ${detail.slice(0, 200)}` : ''}`
    );
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  return res.json();
}

// In-flight refresh dedupe so concurrent getToken() callers share one request.
let refreshInFlight = null;

async function doRefresh() {
  const currentRefresh = localStorage.getItem(LS.refreshToken);
  if (!currentRefresh) {
    clearAuthStorage();
    return null;
  }
  try {
    const tokens = await postToken({
      grant_type: 'refresh_token',
      refresh_token: currentRefresh,
      client_id: clientId,
    });
    const expiresAt = persistTokens(tokens);
    scheduleRefresh(expiresAt, doRefresh);
    return tokens.id_token || null;
  } catch (err) {
    // Multi-tab race: when refresh-token rotation is enabled (Authentik default),
    // a sibling tab may have refreshed first and rotated our token out from
    // under us. If localStorage now holds a different refresh_token AND a
    // still-fresh expiry, trust that refresh and return the current id_token
    // instead of nuking the session.
    const stored = localStorage.getItem(LS.refreshToken);
    const storedExpiresAt = Number(localStorage.getItem(LS.expiresAt) || 0);
    if (
      stored &&
      stored !== currentRefresh &&
      storedExpiresAt > Date.now() + 60_000
    ) {
      return localStorage.getItem(LS.idToken);
    }

    // Only clear on a definitive auth failure. Transient failures (network
    // drop, 5xx, CORS preflight glitch) leave storage intact so the next
    // getToken() call retries on demand, a brief Wi-Fi blip should not
    // log the user out.
    const isAuthFailure =
      err?.status >= 400 &&
      err?.status < 500 &&
      (err?.body?.error === 'invalid_grant' ||
        err?.body?.error === 'invalid_token');
    if (isAuthFailure) {
      console.error(
        '[auth] refresh definitively failed, clearing tokens:',
        err.body?.error
      );
      clearAuthStorage();
      return null;
    }
    console.warn(
      '[auth] refresh transiently failed, will retry on next getToken:',
      err?.message
    );
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API.
// ---------------------------------------------------------------------------

export async function login({ email, returnTo, flow } = {}) {
  if (!baseUrl || !clientId || !appSlug || !redirectUri) {
    throw new Error('authentik_env_missing');
  }

  // Generate PKCE.
  const verifier = randomUrlSafe(64);
  const challenge = await deriveChallenge(verifier);
  const state = randomUrlSafe(32);

  try {
    sessionStorage.setItem(SS.pkceVerifier, verifier);
    sessionStorage.setItem(SS.state, state);
    if (returnTo) sessionStorage.setItem(SS.returnTo, returnTo);
    else sessionStorage.removeItem(SS.returnTo);
  } catch (err) {
    console.error('[auth] sessionStorage write failed:', err);
  }

  // Enrollment flow: route customers through signup. Authentik continues
  // the OAuth code-flow once enrollment finishes.
  let url;
  if (flow === 'enrollment') {
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    const qs = params.toString();
    url = `${baseUrl}/if/flow/${appSlug}-enrollment/${qs ? `?${qs}` : ''}`;
  } else {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: 'openid email profile',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    });
    if (email) params.set('login_hint', email);
    url = `${baseUrl}/application/o/authorize/?${params.toString()}`;
  }

  window.location.href = url;
}

export async function handleCallback() {
  if (!baseUrl || !clientId || !redirectUri) {
    throw new Error('authentik_env_missing');
  }

  const search = new URLSearchParams(window.location.search);
  const code = search.get('code');
  const state = search.get('state');
  const error = search.get('error');
  if (error) {
    throw new Error(`authentik_error: ${error}`);
  }
  if (!code) {
    throw new Error('missing_code');
  }

  const expectedState = sessionStorage.getItem(SS.state);
  if (!expectedState || expectedState !== state) {
    throw new Error('state_mismatch');
  }

  if (isConsumed(code)) {
    // Page-reload of the callback URL, silently bail so the user
    // ends up on the app and not stuck with an exchanged code.
    const returnTo = sessionStorage.getItem(SS.returnTo) || '/app';
    return { returnTo };
  }

  const verifier = sessionStorage.getItem(SS.pkceVerifier);
  if (!verifier) {
    throw new Error('missing_pkce_verifier');
  }

  let tokens;
  try {
    tokens = await postToken({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: verifier,
    });
  } finally {
    // Mark consumed even on failure so a stuck retry doesn't loop.
    markConsumed(code);
  }

  const expiresAt = persistTokens(tokens);
  scheduleRefresh(expiresAt, doRefresh);

  // Detect MS-federated session by inspecting id_token issuer; flag for logout-chain.
  try {
    if (tokens.id_token) {
      const claims = decodeJwt(tokens.id_token);
      const iss = String(claims?.iss || '');
      if (iss.includes('login.microsoftonline.com') || iss.includes('sts.windows.net')) {
        localStorage.setItem(LS.viaMs, '1');
      } else {
        localStorage.removeItem(LS.viaMs);
      }
    }
  } catch {
    // non-fatal
  }

  const returnTo = sessionStorage.getItem(SS.returnTo) || '/app';

  // Clean PKCE state, these are single-use.
  try {
    sessionStorage.removeItem(SS.pkceVerifier);
    sessionStorage.removeItem(SS.state);
    sessionStorage.removeItem(SS.returnTo);
  } catch {
    // ignore
  }

  return { returnTo };
}

export async function getToken() {
  const idToken = localStorage.getItem(LS.idToken);
  const expiresAt = getStoredExpiry();
  if (!idToken || !expiresAt) return null;

  // Plenty of time left → use cached.
  if (expiresAt - Date.now() > 60_000) return idToken;

  // About to expire, refresh (with in-flight dedupe).
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export function isAuthenticated() {
  const idToken = localStorage.getItem(LS.idToken);
  const expiresAt = getStoredExpiry();
  return Boolean(idToken) && expiresAt > Date.now();
}

export function getUser() {
  const idToken = localStorage.getItem(LS.idToken);
  if (!idToken) return null;
  try {
    const claims = decodeJwt(idToken);
    return {
      sub: claims.sub,
      email: claims.email,
      name: claims.name,
      groups: claims.groups || [],
    };
  } catch (err) {
    console.error('[auth] failed to decode id_token:', err);
    return null;
  }
}

export async function logout() {
  const idToken = localStorage.getItem(LS.idToken);
  // NOTE: HAI Job is not Microsoft-federated today, so we skip the
  // MS-first chain described in design §8.2 and go straight to
  // Authentik's end-session flow. The viaMs flag is set during
  // callback for a future opt-in.
  clearAuthStorage();

  if (!baseUrl || !appSlug) {
    window.location.href = `${window.location.origin}/`;
    return;
  }

  let url;
  if (idToken) {
    url = `${baseUrl}/if/flow/default-invalidation/?token=${encodeURIComponent(idToken)}`;
  } else {
    url = `${baseUrl}/if/session-end/${appSlug}/`;
  }
  window.location.href = url;
}
